import React from "react";
import { Box, Typography } from "@mui/material";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.72)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const SECTIONS = [
  {
    key: "positive",
    label: "Strengths",
    accent: "#1f8a5a",
    accentSoft: "rgba(31, 138, 90, 0.12)"
  },
  {
    key: "constructive",
    label: "Watch-outs",
    accent: "#c25a1f",
    accentSoft: "rgba(194, 90, 31, 0.12)"
  },
  {
    key: "improvement",
    label: "Next time",
    accent: "#3854e8",
    accentSoft: "rgba(56, 84, 232, 0.12)"
  }
];

const Section = ({ index, label, accent, accentSoft, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Box
      sx={{
        position: "relative",
        py: 2.5,
        pl: 2.5,
        borderLeft: `2px solid ${accent}`
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1.5
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.68rem",
            fontWeight: 500,
            letterSpacing: "0.16em",
            color: INK_FAINT,
            fontVariantNumeric: "tabular-nums"
          }}
        >
          {String(index).padStart(2, "0")}
        </Typography>
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "0.78rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: INK
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: "999px",
            backgroundColor: accentSoft,
            color: accent,
            fontFamily: MONO,
            fontSize: "0.66rem",
            fontWeight: 500,
            letterSpacing: "0.08em",
            fontVariantNumeric: "tabular-nums"
          }}
        >
          {items.length}
        </Box>
      </Box>
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.25,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        {items.map((item, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              fontFamily: SANS,
              fontSize: "0.95rem",
              lineHeight: 1.55,
              color: INK_SOFT,
              "&::marker": { color: accent }
            }}
          >
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const formatRelative = at => {
  if (!at) return null;
  const then = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(then.getTime())) return null;
  const secs = Math.max(0, Math.round((Date.now() - then.getTime()) / 1000));
  if (secs < 60) return "Just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.round(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

export default function Feedback(props) {
  let parsed;
  try {
    parsed = JSON.parse(props.feedback);
  } catch (e) {
    return (
      <Box
        sx={{
          border: `1px solid ${INK_HAIRLINE}`,
          borderRadius: "16px",
          p: 2.5,
          backgroundColor: "#fff",
          color: INK_SOFT
        }}
      >
        Couldn't parse feedback — try resubmitting your answer.
      </Box>
    );
  }

  const sections = SECTIONS.map((s, i) => ({
    ...s,
    index: i + 1,
    items: Array.isArray(parsed[s.key]) ? parsed[s.key] : []
  }));
  const hasAny = sections.some(s => s.items.length > 0);
  const relative = formatRelative(props.generatedAt);

  return (
    <Box
      sx={{
        border: `1px solid ${INK_HAIRLINE}`,
        borderRadius: "18px",
        backgroundColor: "#fff",
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 12px 32px -22px rgba(15, 17, 33, 0.18)",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.75,
          px: 2.5,
          borderBottom: `1px solid ${INK_HAIRLINE}`,
          backgroundColor: "rgba(15, 17, 33, 0.02)"
        }}
      >
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "0.92rem",
            fontWeight: 600,
            color: INK
          }}
        >
          Written feedback
        </Typography>
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.7rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: INK_FAINT
          }}
        >
          {relative ? `AI · ${relative}` : "AI feedback"}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 0.5 }}>
        {hasAny ? (
          sections.map(s => <Section key={s.key} {...s} />)
        ) : (
          <Box sx={{ py: 3, color: INK_FAINT }}>
            <Typography sx={{ fontFamily: SANS, fontSize: "0.95rem" }}>
              No notes returned. Try a longer answer next time.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
